// Global guard for lightweight-charts' "Object is disposed" error.
//
// The throw originates INSIDE fancy-canvas' own ResizeObserver callback
// (DevicePixelContentBoxBinding.resizeCanvasElement), fired AFTER a chart was
// disposed during a fast widget drag/resize/remount. Because it surfaces from an
// async observer callback — not inside any React render — a React ErrorBoundary
// cannot catch it. Left unhandled it bubbles to window.onerror, which React dev
// overlay / our boundary treat as a fatal page error, producing the constant
// error screen users saw while reorganizing widgets.
//
// We narrowly swallow ONLY this known-benign, library-internal disposal race.
// Anything else passes through untouched.

const DISPOSED_RE = /Object is disposed/i;

/** True for the known-benign lightweight-charts disposal-race error only. */
export function isDisposedError(value: unknown): boolean {
  if (value instanceof Error) return DISPOSED_RE.test(value.message);
  if (typeof value === "string") return DISPOSED_RE.test(value);
  return false;
}

let installed = false;

export function installChartErrorGuard(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // Capture phase so we run before React's error overlay / other listeners and
  // can stop the propagation of this specific benign error.
  window.addEventListener(
    "error",
    (event: ErrorEvent) => {
      if (isDisposedError(event.error) || isDisposedError(event.message)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true,
  );

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    if (isDisposedError(event.reason)) {
      event.preventDefault();
    }
  });
}
